import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';
import './Footer.css';
import { makeApiRequest, API_ENDPOINTS } from '../config/api';

interface ContactInfo {
  email: string;
  phone: string;
  address?: string;
}

interface ContactData {
  page: {
    content: {
      contact_info: ContactInfo;
    };
  };
}

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await makeApiRequest(API_ENDPOINTS.PAGES_BY_ID('contact'));
        if (response.ok) {
          const data: ContactData = await response.json();
          setContactInfo(data.page.content.contact_info);
        }
      } catch (err) {
        console.error('Error fetching contact info for footer:', err);
      }
    };

    fetchContactInfo();
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 100;
      const elementPosition = element.offsetTop;
      const offsetPosition = elementPosition - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleNavigation = (path: string, sectionId?: string) => {
    if (path === '/') {
      // Navigate to home
      if (location.pathname === '/') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate('/');
      }
    } else if (path === '/products') {
      // Navigate to products section
      if (location.pathname === '/' || location.pathname === '/products') {
        scrollToSection('products');
      } else {
        navigate('/');
        setTimeout(() => scrollToSection('products'), 100);
      }
    } else {
      // Navigate to other pages
      navigate(path);
    }
  };

  // Parse phone numbers from the phone string - extract all numbers starting with +
  const phoneNumbers = contactInfo?.phone
    ? contactInfo.phone.match(/\+\d+[\s\d]*/g)?.map(p => p.trim()).filter(p => p.length > 0) || []
    : [];

  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-content">
          <div className="footer-brand">
            <Logo size="large" showText={false} variant="dark" />
            <p className="footer-tagline">
            </p>
          </div>
          
          <div className="footer-links">
            <div className="footer-section">
              <h4>Company</h4>
              <ul>
                <li><button onClick={() => handleNavigation('/')} className="footer-link">Home</button></li>
                <li><button onClick={() => handleNavigation('/products')} className="footer-link">Products</button></li>
                <li><button onClick={() => handleNavigation('/industries')} className="footer-link">Industries</button></li>
                <li><button onClick={() => handleNavigation('/about')} className="footer-link">About</button></li>
                <li><button onClick={() => handleNavigation('/contact')} className="footer-link">Contact</button></li>
              </ul>
            </div>
            
            <div className="footer-section">
              <h4>Contact</h4>
              <div className="contact-info">
                {contactInfo?.email && <p>{contactInfo.email}</p>}
                {phoneNumbers.length > 0 && (
                  <div className="footer-phone-numbers">
                    {phoneNumbers.map((phone, index) => (
                      <React.Fragment key={index}>
                        <a href={`tel:${phone.replace(/\s+/g, '')}`}>{phone}</a>
                        {index < phoneNumbers.length - 1 && <span className="footer-phone-separator"> | </span>}
                      </React.Fragment>
                    ))}
                  </div>
                )}
                {contactInfo?.address && <p>{contactInfo.address}</p>}
              </div>
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>&copy; 2024 AxiomTeq. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 